import type { RefObject } from 'react'
import { useLayoutEffect } from 'react'

import { logError } from '@core/logging'
import { ensureResizeObserver } from '@player/utils/resizeObserverPromise'

import { useStatefulRef } from '../useStatefulRef'

/**
 * Allows users to trigger callback function on resizing for an element.
 *
 * By default it returns its own ref to attach to the element to observe. An
 * existing `targetRef` can be passed instead, which is useful when the element
 * is already referenced for other purposes (scroll handlers, drag, etc.). In
 * that case attach `targetRef` to the element and ignore the returned ref.
 *
 * @example
 * // Self-owned ref
 * const ref = useResizeObserver(callbackFn)
 * return <div ref={ref}>Div to be measured</div>
 *
 * @example
 * // Existing external ref
 * const ref = useRef<HTMLDivElement>(null)
 * useResizeObserver(callbackFn, ref)
 * return <div ref={ref}>Div to be measured</div>
 */
export function useResizeObserver<T extends HTMLElement>(
  callbackFn: ResizeObserverCallback,
  targetRef?: RefObject<HTMLElement | null>,
) {
  const ownRef = useStatefulRef<T | null>(null)
  const ref = targetRef ?? ownRef

  useLayoutEffect(() => {
    let observerInstance: ResizeObserver
    let rafId = 0
    // ensureResizeObserver() may resolve after this effect has cleaned up (e.g.
    // on the polyfill path). Guard against attaching a stale observer.
    let isStale = false
    // Defer the callback to the next frame so layout changes it triggers don't
    // re-enter the observer within the same delivery cycle, which causes
    // "ResizeObserver loop completed with undelivered notifications".
    const deferredCallback = (...args: Parameters<ResizeObserverCallback>) => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => callbackFn(...args))
    }
    ensureResizeObserver()
      .then((ResizeObserver) => {
        if (isStale) {
          return
        }
        observerInstance = new ResizeObserver(deferredCallback)
        // Re-read the current element: for an external ref it may only be
        // attached after this component renders but before the layout effect.
        const target = ref.current
        if (!target) {
          return
        }
        observerInstance.observe(target)
      })
      .catch(logError)
    return () => {
      isStale = true
      cancelAnimationFrame(rafId)
      observerInstance?.disconnect()
    }
    // ref.current (own stateful ref) triggers a rerender on attach, hence it
    // should be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, callbackFn])

  return ownRef
}
